/**
 * 背景音：海浪环境音 + 舒缓音乐，两路**完全独立**的开关和音量。
 *
 * 用 `HTMLAudioElement` 而不是 WebAudio：
 *  - loop / volume 现成，不需要自己管缓冲
 *  - 浏览器自动播放策略要求"用户手势后"才能播，所以统一在第一次点击/按键后启动
 *  - 素材缺失（404）时静默失败，不影响游戏
 */

export interface BgmSettings {
  ocean: boolean;
  music: boolean;
  /** 海浪音量 0..1 */
  oceanVolume: number;
  /** 音乐音量 0..1 */
  musicVolume: number;
}

const STORAGE_KEY = 'sea.bgm.v2';

const DEFAULTS: BgmSettings = {
  ocean: true,
  music: true,
  // 海浪默认调低：实测它比音乐响很多
  oceanVolume: 0.35,
  musicVolume: 0.55,
};

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

export class Bgm {
  private readonly ocean: HTMLAudioElement;
  private readonly music: HTMLAudioElement;
  private settings: BgmSettings;
  private started = false;

  constructor() {
    this.settings = load();
    this.ocean = this.make('/audio/ocean.mp3');
    this.music = this.make('/audio/music.mp3');
    this.applyVolumes();
  }

  private make(src: string): HTMLAudioElement {
    const audio = new Audio(src);
    audio.loop = true;
    audio.preload = 'auto';
    return audio;
  }

  private applyVolumes(): void {
    this.ocean.volume = clamp01(this.settings.oceanVolume);
    this.music.volume = clamp01(this.settings.musicVolume);
  }

  /** 调试：两路播放器的实际状态 */
  debugInfo(): unknown {
    const dump = (a: HTMLAudioElement) => ({
      src: (a.currentSrc || a.src).split('/').pop(),
      paused: a.paused,
      readyState: a.readyState,
      duration: Number.isFinite(a.duration) ? Math.round(a.duration) : null,
      currentTime: +a.currentTime.toFixed(2),
      volume: +a.volume.toFixed(2),
      error: a.error ? a.error.code : null,
    });
    return { started: this.started, settings: this.current, ocean: dump(this.ocean), music: dump(this.music) };
  }

  get current(): BgmSettings {
    return { ...this.settings };
  }

  /** 在用户首次交互时调用（浏览器要求） */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.sync();
  }

  set(patch: Partial<BgmSettings>): BgmSettings {
    this.settings = { ...this.settings, ...patch };
    save(this.settings);
    this.applyVolumes();
    this.sync();
    return this.current;
  }

  private sync(): void {
    if (!this.started) return;
    this.playIf(this.ocean, this.settings.ocean);
    this.playIf(this.music, this.settings.music);
  }

  private playIf(audio: HTMLAudioElement, enabled: boolean): void {
    if (!enabled) {
      audio.pause();
      return;
    }
    // play() 返回的 Promise 可能因为自动播放策略被拒绝，忽略即可
    void audio.play().catch(() => {});
  }
}

function load(): BgmSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<BgmSettings>;
    return {
      ocean: parsed.ocean ?? DEFAULTS.ocean,
      music: parsed.music ?? DEFAULTS.music,
      oceanVolume:
        typeof parsed.oceanVolume === 'number' ? clamp01(parsed.oceanVolume) : DEFAULTS.oceanVolume,
      musicVolume:
        typeof parsed.musicVolume === 'number' ? clamp01(parsed.musicVolume) : DEFAULTS.musicVolume,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

function save(settings: BgmSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* 隐私模式等场景忽略 */
  }
}
